import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  DiscardModal,
  FlexContainers,
  Paragraph2,
  SmallButton,
  WrapperContainer
} from "../../../components";
import {
  alignItems,
  routePaths,
  strCode,
  releaseNoteOptions as releaseNoteOptionNames
} from "../../../consts";
import { log, navigationService, notificationService, t } from "../../../services";
import { releaseNoteUtils } from "./utils/releaseNoteUtils";
import { objectUtil } from "../../../utils";

import SignatureFile from "./components/SignatureFile";
import FWUName from "./components/FWUName";
import BinFile from "./components/BinFile";
import Device from "./components/Device";
import { ReleaseNotes } from "./components/ReleaseNotes/ReleaseNotes";

import { FormSection } from "../../../templates";
import { FormWrapper } from "./styled";

const emptyFwVersion = {
  fwuVersion: {
    major: null,
    minor: null,
    patch: null
  },
  model: "",
  deviceType: "",
  binFile: null,
  signatureFile: null,
  isNew: true
};

const NewFirmwareVerionForm = ({
  fwuVersionData,
  releaseNotes,
  isLoading,
  releaseNotesLoading,
  onSaveFwVersion,
  onSaveReleaseNotes
}) => {
  const [fwuVersion, setFwuVersion] = useState(emptyFwVersion);

  const [localizations, setLocalizations] = useState([]);
  const [iconsData, setIconsData] = useState([]);
  const [thumbnailsData, setThumbnailsData] = useState([]);

  const [selectedModelOptionId, setSelectedModelOptionId] = useState("");
  const [selectedTypeOptionId, setSelectedTypeOptionId] = useState("");

  const [isValid, setIsValid] = useState(true);
  const [isSameData, setIsSameData] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [missingFields, setMissingFields] = useState({});

  const [releaseNoteOption, setReleaseNoteOption] = useState(releaseNoteOptionNames.SIMPLE_LIST);

  const [newReleaseNoteOption, setNewReleaseNoteOption] = useState(null);
  const [changeTypeModalActive, setChangeTypeModalActive] = useState(false);

  const originalReleaseNotesType = useMemo(() => {
    if (releaseNotes) {
      return releaseNotes?.releaseNotesType;
    }
  }, [releaseNotes]);

  const originalLocalizations = useMemo(() => {
    if (releaseNotes?.localizations) {
      return releaseNotes?.localizations;
    }
  }, [releaseNotes]);

  const media = useMemo(() => {
    if (iconsData?.length && releaseNoteOption !== releaseNoteOptionNames.SIMPLE_LIST) {
      const iconsList = [];

      iconsData.forEach((media) => {
        if (media?.icon?.name) {
          iconsList.push({
            id: media?.id,
            media: media?.icon,
            mediaExtension: media?.mediaExtension
          });
        }
      });

      return iconsList;
    }

    return [];
  }, [iconsData, releaseNoteOption]);

  const thumbnails = useMemo(() => {
    if (thumbnailsData?.length && releaseNoteOption !== releaseNoteOptionNames.SIMPLE_LIST) {
      const thumbnailsList = [];

      thumbnailsData.forEach((media) => {
        if (media?.media?.name) {
          thumbnailsList.push({
            id: media?.id,
            media: media?.media,
            mediaExtension: media?.mediaExtension
          });
        }
      });

      return thumbnailsList;
    }

    return [];
  }, [thumbnailsData, releaseNoteOption]);

  const isEdit = useMemo(() => {
    return !!fwuVersionData;
  }, [fwuVersionData]);

  const getNewFwVersionData = useCallback(() => {
    let newData = {
      version: Object.values(fwuVersion.fwuVersion).join("."),
      model: fwuVersion.model,
      device_type: fwuVersion.deviceType
    };

    return newData;
  }, [fwuVersion]);

  const isFwVersionValid = useMemo(() => {
    const versionsValues = Object.values(fwuVersion?.fwuVersion || {});

    return !!(
      versionsValues.length === 3 &&
      versionsValues.every((version) => version !== null) &&
      fwuVersion?.model &&
      fwuVersion?.deviceType &&
      fwuVersion?.binFile?.name &&
      fwuVersion?.signatureFile?.name
    );
  }, [fwuVersion]);

  useEffect(() => {
    let valid = isFwVersionValid && !!localizations.length;
    let isSame = false;

    const localisationErrorFields = releaseNoteUtils.getReleaseNoteErrors(
      localizations,
      iconsData,
      releaseNoteOption
    );

    if (localisationErrorFields?.length) {
      valid = false;
    }

    const errorFields = {
      localizations: localisationErrorFields
    };

    setMissingFields(errorFields);
    setIsValid(valid);

    if (originalReleaseNotesType && originalLocalizations) {
      const newData = releaseNoteUtils.formatReleaseNotes(localizations, releaseNoteOption);

      if (
        objectUtil.objEquals(originalLocalizations, newData) &&
        releaseNoteOption === originalReleaseNotesType
      ) {
        isSame = true;
      }
    }

    setIsSameData(isSame);
  }, [
    isFwVersionValid,
    localizations,
    iconsData,
    thumbnailsData,
    releaseNoteOption,
    originalReleaseNotesType,
    originalLocalizations
  ]);

  useEffect(() => {
    resetFormData();
    prepopulateData();

    return () => {
      resetFormData();
    };
  }, [fwuVersionData, releaseNotes]);

  const prepopulateData = useCallback(() => {
    if (fwuVersionData) {
      if (fwuVersionData?.binFile) {
        fwuVersionData.binFile.uploaded = true;
      }

      if (fwuVersionData?.signatureFile) {
        fwuVersionData.signatureFile.uploaded = true;
      }

      if (fwuVersion?.binFile) {
        fwuVersionData.binFile = fwuVersion?.binFile;
      }

      if (fwuVersion?.signatureFile) {
        fwuVersionData.signatureFile = fwuVersion?.signatureFile;
      }

      setFwuVersion(fwuVersionData);

      setSelectedModelOptionId(fwuVersionData?.model);
      setSelectedTypeOptionId(fwuVersionData?.deviceType?.toLowerCase());

      if (releaseNotes) {
        const {
          type: releaseNotesType,
          localizations: localizationsData,
          media,
          thumbnails
        } = releaseNoteUtils.parseReleaseNotes(releaseNotes);

        if (releaseNotesType && localizationsData) {
          setReleaseNoteOption(releaseNotesType);
          setLocalizations(localizationsData);
          setIconsData(media);
          setThumbnailsData(thumbnails);
        }
      }
    }
  }, [fwuVersionData, fwuVersion, releaseNotes]);

  const resetFormData = () => {
    setFwuVersion(emptyFwVersion);
    setSelectedModelOptionId("");
    setSelectedTypeOptionId("");
    setLocalizations([]);
  };

  const handleChangeReleaseNoteOption = (newReleaseNoteOption) => {
    const isEmpty = releaseNoteUtils.getIsReleaseNoteEmpty(localizations, releaseNoteOption);
    const shouldResetData = [releaseNoteOption, newReleaseNoteOption].includes(
      releaseNoteOptionNames.SIMPLE_LIST
    );

    if (!isEmpty && shouldResetData) {
      setChangeTypeModalActive(true);
      setNewReleaseNoteOption(newReleaseNoteOption);
    } else {
      setReleaseNoteOption(newReleaseNoteOption);

      if (shouldResetData) {
        setLocalizations([]);
        setIconsData([]);
      }
    }
  };

  const onChangeReleaseNoteOption = () => {
    if (newReleaseNoteOption) {
      setReleaseNoteOption(newReleaseNoteOption);
      setLocalizations([]);
      setIconsData([]);
      setNewReleaseNoteOption(null);

      setChangeTypeModalActive(false);
    }
  };

  const onClose = () => {
    navigationService.forwardTo(routePaths.FLEET_MANAGEMENT);
  };

  const onSubmit = async () => {
    if (!showErrors) {
      setShowErrors(true);
    }

    if (!isValid) {
      log.warn("NewFirmwareVerionForm: Unable to save fw version due to missing fields");

      notificationService.showError({
        title: t(strCode.MISSING_FIELDS),
        message: t(strCode.MISSING_FIELDS_MESSAGE)
      });
      return;
    }

    try {
      const data = getNewFwVersionData();

      const formattedLocalizations = releaseNoteUtils.formatReleaseNotes(
        localizations,
        releaseNoteOption
      );
      const isNew = !releaseNotes;

      await onSaveFwVersion(data, fwuVersion.binFile, fwuVersion.signatureFile);

      const hasUpdatedThumbnails = releaseNotes?.thumbnail?.length !== thumbnailsData?.length;

      if (!isSameData || media?.length || thumbnails?.length || hasUpdatedThumbnails) {
        const releaseNotesData = {
          version: data?.version,
          releaseNotesType: releaseNoteOption,
          localizations: formattedLocalizations,
          mediaData: releaseNoteUtils.formatMediaData(iconsData),
          thumbnails: releaseNoteUtils.formatMediaData(thumbnailsData)
        };

        if (formattedLocalizations || !isNew) {
          await onSaveReleaseNotes(releaseNotesData, { media, thumbnails }, isNew);
        }
      }

      resetFormData();
      navigationService.forwardTo(routePaths.FLEET_MANAGEMENT);
    } catch (e) {
      log.error(`An error occurred while saving fw version data.`, e);
    }
  };

  return (
    <>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width={"100%"}
        height={"100%"}>
        {releaseNotesLoading ? (
          <WrapperContainer
            Container={FlexContainers.FlexColumnCenter}
            width={"100%"}
            mt={200}
            textAlign={"center"}>
            <Paragraph2>{t(strCode.LOADING)}</Paragraph2>
          </WrapperContainer>
        ) : (
          <FormWrapper Container={FlexContainers.FlexColumnBetween} width={"100%"} mt={32}>
            <WrapperContainer
              Container={FlexContainers.FlexRowStart}
              alignItems={alignItems.start}
              width={"100%"}
              height={"100%"}
              gap={16}>
              <WrapperContainer
                Container={FlexContainers.FlexColumnStart}
                width={"40%"}
                minWidth={450}
                height={"100%"}
                gap={16}>
                <FormSection title={t(strCode.FIRMWARE_VERSION_TITLE)}>
                  <FWUName
                    isLoading={isLoading || isEdit}
                    setFwuVersion={setFwuVersion}
                    fwuVersion={fwuVersion}
                  />
                </FormSection>

                <FormSection title={t(strCode.DEVICE_TITLE)}>
                  <Device
                    isLoading={isLoading || isEdit}
                    setFwuVersion={setFwuVersion}
                    fwuVersion={fwuVersion}
                    selectedModelOptionId={selectedModelOptionId}
                    setSelectedModelOptionId={setSelectedModelOptionId}
                    selectedTypeOptionId={selectedTypeOptionId}
                    setSelectedTypeOptionId={setSelectedTypeOptionId}
                  />

                  <BinFile
                    isLoading={isLoading || (isEdit && fwuVersionData?.binFile?.name)}
                    setFwuVersion={setFwuVersion}
                    fwuVersion={fwuVersion}
                  />

                  <SignatureFile
                    isLoading={isLoading || (isEdit && fwuVersionData?.signatureFile?.name)}
                    setFwuVersion={setFwuVersion}
                    fwuVersion={fwuVersion}
                  />
                </FormSection>
              </WrapperContainer>

              <ReleaseNotes
                releaseNoteOption={releaseNoteOption}
                loading={releaseNotesLoading}
                localizations={localizations}
                iconsData={iconsData}
                thumbnailsData={thumbnailsData}
                missingFields={missingFields}
                showErrors={showErrors}
                setLocalizations={setLocalizations}
                setIconsData={setIconsData}
                setThumbnailsData={setThumbnailsData}
                onChangeOption={handleChangeReleaseNoteOption}
              />
            </WrapperContainer>

            <WrapperContainer Container={FlexContainers.FlexRowEnd} gap={8} width={"100%"} pt={24}>
              <SmallButton disabled={isLoading} secondary onClick={onClose}>
                {t(strCode.DISCARD_BTN)}
              </SmallButton>

              <SmallButton
                disabled={!isFwVersionValid || isLoading || releaseNotesLoading}
                loading={isLoading}
                onClick={onSubmit}>
                {t(strCode.SAVE_BTN)}
              </SmallButton>
            </WrapperContainer>
          </FormWrapper>
        )}
      </WrapperContainer>

      <DiscardModal
        title={strCode.CHANGE_TYPE_TITLE}
        text={strCode.CHANGE_TYPE_DESCRIPTION}
        confirmBtnText={strCode.YES_CHANGE_BTN}
        active={changeTypeModalActive}
        setActive={setChangeTypeModalActive}
        onDiscard={onChangeReleaseNoteOption}
      />
    </>
  );
};

NewFirmwareVerionForm.propTypes = {
  fwuVersionData: PropTypes.shape({
    fwuName: PropTypes.string.isRequired,
    fwuVersion: PropTypes.shape({
      major: PropTypes.string.isRequired,
      minor: PropTypes.string.isRequired,
      patch: PropTypes.string.isRequired
    }),
    deviceType: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    binFile: PropTypes.object,
    signatureFile: PropTypes.object
  }),
  releaseNotes: PropTypes.object,
  isLoading: PropTypes.bool,
  releaseNotesLoading: PropTypes.bool,
  onSaveFwVersion: PropTypes.func.isRequired,
  onSaveReleaseNotes: PropTypes.func.isRequired
};

export default NewFirmwareVerionForm;
